import { FolderCheck, BarChart3, ArrowRight } from 'lucide-react';
import { GithubIcon } from '@/components/icons/BrandIcons';
import { useInView } from '@/hooks/useInView';

const steps = [
  {
    number: '01',
    icon: GithubIcon,
    title: 'Authorize GitHub',
    description: 'Securely connect with GitHub OAuth. We only request read-only access to repositories you choose.',
    detail: 'We never see your passwords or gain write access to your code.',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
  },
  {
    number: '02',
    icon: FolderCheck,
    title: 'Select Repositories',
    description: 'Choose which repositories to analyze. You can add or remove repos at any time.',
    detail: 'We automatically sync 6 months of historical data.',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  {
    number: '03',
    icon: BarChart3,
    title: 'See Insights',
    description: 'View DORA metrics, cycle times, team health scores, and more within minutes.',
    detail: 'Data syncs automatically every 15 minutes.',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
];

export default function HowItWorks() {
  const [headerRef, isHeaderInView] = useInView<HTMLDivElement>({ threshold: 0.2 });
  const [stepsRef, isStepsInView] = useInView<HTMLDivElement>({ threshold: 0.1 });


  // Animation delay for each step card
  const getStepDelay = (index: number) => {
    const delays = ['animation-delay-0', 'animation-delay-150', 'animation-delay-300'];
    return delays[index] || 'animation-delay-0';
  };

  return (
    <section id="how-it-works" className="py-12 bg-gray-50 dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-10 ${
            isHeaderInView ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Three steps. No agents. No code changes.
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            No 3-month implementation project. No dedicated admin. Just GitHub OAuth and go.
          </p>
        </div>

        {/* Steps */}
        <div ref={stepsRef} className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`relative ${
                isStepsInView ? `animate-fade-in-up ${getStepDelay(index)}` : 'opacity-0'
              }`}
            >
              {/* Connector line (hidden on mobile, shown on desktop) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-16 left-[60%] w-[80%] h-0.5 bg-gray-200 dark:bg-gray-600">
                  <ArrowRight className="absolute -right-3 -top-2 w-5 h-5 text-gray-300 dark:text-gray-500" />
                </div>
              )}

              <div className="bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-sm border border-gray-100 dark:border-gray-600 h-full transition-shadow hover:shadow-md">
                {/* Step Number */}
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-4xl font-bold text-gray-300 dark:text-gray-500">{step.number}</span>
                  <div className={`p-3 ${step.bgColor} dark:bg-opacity-20 rounded-xl`}>
                    <step.icon className={`w-6 h-6 ${step.color}`} />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {step.description}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  {step.detail}
                </p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
